import type {
	DataListColumnVisibility,
	DataListColumnWidth,
} from '@arritech/ui/DataList/DataList.model';
import type {
	AdvancedFilterCollapsePreferences,
	AdvancedFilterLayoutPreferences,
	ColorMode,
	DataListPreferences,
	FilesTableMetaItemKey,
	FilesTableMetaVisibility,
	FilesTablePreferences,
	FilesTableSortDirection,
	FilesTableSortPreference,
	SplitPanelWidthPreferences,
	SplitRouteMemoryPreferences,
	UserPreferenceBasePayload,
} from './settings.model';
import {
	ADVANCED_FILTER_COLLAPSE_STORAGE_KEY,
	ADVANCED_FILTER_LAYOUTS_STORAGE_KEY,
	COLOR_MODE_STORAGE_KEY,
	CUSTOM_FIELDS_MANAGER_DEFAULT_FIELDS_VISIBLE_STORAGE_KEY,
	DATA_LIST_PREFERENCES_STORAGE_KEY,
	DEFAULT_CLOCK_FORMAT,
	DEFAULT_DATE_FORMAT,
	DEFAULT_NAME_FORMAT,
	DEFAULT_TIME_ZONE,
	FILES_TABLE_PREFERENCES_STORAGE_KEY,
	PREFERRED_CLOCK_FORMAT_STORAGE_KEY,
	PREFERRED_DATE_FORMAT_STORAGE_KEY,
	PREFERRED_NAME_FORMAT_STORAGE_KEY,
	PREFERRED_TIME_ZONE_STORAGE_KEY,
	SPLIT_PANEL_WIDTHS_STORAGE_KEY,
	SPLIT_ROUTE_MEMORY_STORAGE_KEY,
	SPLIT_VIEW_ENABLED_STORAGE_KEY,
} from './settings.constants';

/**
 * Returns `true` when value is a supported color mode.
 */
export const isColorMode = (value: unknown): value is ColorMode =>
	value === 'light' || value === 'dark';

/**
 * Returns `true` when value is a supported DataList sort order.
 */
export const isDataListSortOrder = (
	value: unknown
): value is '' | 'ascending' | 'descending' =>
	value === 'ascending' || value === 'descending' || value === '';

/**
 * Runtime guard for plain object records.
 */
export const isRecord = (value: unknown): value is Record<string, unknown> =>
	Boolean(value) && typeof value === 'object' && !Array.isArray(value);

/**
 * Safe `hasOwnProperty` check for records.
 */
export const hasOwn = (value: Record<string, unknown>, key: string): boolean =>
	Object.prototype.hasOwnProperty.call(value, key);

/**
 * Compares two string arrays by order and value.
 */
export const isSameArray = (a: string[] = [], b: string[] = []): boolean =>
	a.length === b.length && a.every((entry, index) => entry === b[index]);

/**
 * Returns `true` when value is a supported files-table sort key.
 */
export const isFilesTableSortPreference = (
	value: unknown
): value is FilesTableSortPreference =>
	value === 'fileName' || value === 'uploadedAt' || value === 'uploadedBy';

/**
 * Returns `true` when value is a supported files-table sort direction.
 */
export const isFilesTableSortDirection = (
	value: unknown
): value is FilesTableSortDirection =>
	value === 'ascending' || value === 'descending';

/**
 * Returns default sort direction for a files-table sort key.
 */
export const getDefaultFilesTableSortDirection = (
	sortKey: FilesTableSortPreference
): FilesTableSortDirection =>
	sortKey === 'uploadedAt' ? 'descending' : 'ascending';

/**
 * Returns `true` when value is a supported files-table metadata key.
 */
export const isFilesTableMetaItemKey = (
	value: unknown
): value is FilesTableMetaItemKey =>
	value === 'entityType' ||
	value === 'entityName' ||
	value === 'sessionId' ||
	value === 'uploadedBy' ||
	value === 'createdAt';

/**
 * Normalizes files-table metadata visibility into unique supported entries.
 */
export const normalizeFilesTableMetaVisibility = (
	value: unknown
): FilesTableMetaVisibility[] => {
	if (!Array.isArray(value)) {
		return [];
	}

	const seen = new Set<FilesTableMetaItemKey>();
	return value.reduce<FilesTableMetaVisibility[]>((acc, entry) => {
		if (!isRecord(entry)) {
			return acc;
		}

		const key = entry.key;
		if (!isFilesTableMetaItemKey(key) || seen.has(key)) {
			return acc;
		}

		seen.add(key);
		acc.push({
			key,
			visible: Boolean(entry.visible),
		});
		return acc;
	}, []);
};

/**
 * Normalizes DataList column visibility into unique entries by key.
 */
export const normalizeColumnVisibility = (
	columnVisibility: DataListColumnVisibility[] = []
): DataListColumnVisibility[] => {
	const seen = new Set<string>();
	const normalized: DataListColumnVisibility[] = [];

	columnVisibility.forEach((entry) => {
		if (!entry?.key || seen.has(entry.key)) {
			return;
		}

		seen.add(entry.key);
		normalized.push({
			key: entry.key,
			visible: Boolean(entry.visible),
		});
	});

	return normalized;
};

/**
 * Normalizes DataList column widths into unique positive finite numeric entries.
 */
export const normalizeColumnWidths = (
	columnWidths: DataListColumnWidth[] = []
): DataListColumnWidth[] => {
	const seen = new Set<string>();
	const normalized: DataListColumnWidth[] = [];

	columnWidths.forEach((entry) => {
		if (!entry?.key || seen.has(entry.key)) {
			return;
		}

		const width = Number(entry.width);
		if (!Number.isFinite(width) || width <= 0) {
			return;
		}

		seen.add(entry.key);
		normalized.push({
			key: entry.key,
			width: Math.round(width),
		});
	});

	return normalized;
};

/**
 * Compares two DataList column visibility arrays by order and values.
 */
export const isSameColumnVisibility = (
	a: DataListColumnVisibility[] = [],
	b: DataListColumnVisibility[] = []
): boolean => {
	if (a.length !== b.length) {
		return false;
	}

	return a.every((entry, index) => {
		const compare = b[index];
		return (
			compare &&
			entry.key === compare.key &&
			Boolean(entry.visible) === Boolean(compare.visible)
		);
	});
};

/**
 * Compares two DataList column width arrays by order and values.
 */
export const isSameColumnWidths = (
	a: DataListColumnWidth[] = [],
	b: DataListColumnWidth[] = []
): boolean => {
	if (a.length !== b.length) {
		return false;
	}

	return a.every((entry, index) => {
		const compare = b[index];
		return (
			Boolean(compare) &&
			entry.key === compare.key &&
			Math.round(entry.width) === Math.round(compare.width)
		);
	});
};

/**
 * Normalizes persisted DataList preferences payload.
 */
export const normalizeDataListPreferences = (
	value: unknown
): DataListPreferences => {
	if (!isRecord(value)) {
		return {};
	}

	return Object.entries(value).reduce<DataListPreferences>(
		(acc, [key, entry]) => {
			if (!isRecord(entry)) {
				return acc;
			}

			const nextEntry: DataListPreferences[string] = {};
			const sort = isRecord(entry.sort) ? entry.sort : null;

			if (
				sort &&
				typeof sort.prop === 'string' &&
				sort.prop.trim() &&
				isDataListSortOrder(sort.order) &&
				sort.order
			) {
				nextEntry.sort = {
					prop: sort.prop,
					order: sort.order,
				};
			}

			if (Array.isArray(entry.columnOrder)) {
				const columnOrder = entry.columnOrder.filter(
					(item): item is string =>
						typeof item === 'string' && item.trim().length > 0
				);
				if (columnOrder.length > 0) {
					nextEntry.columnOrder = columnOrder;
				}
			}

			if (Array.isArray(entry.columnVisibility)) {
				const columnVisibility = normalizeColumnVisibility(
					entry.columnVisibility as DataListColumnVisibility[]
				);
				if (columnVisibility.length > 0) {
					nextEntry.columnVisibility = columnVisibility;
				}
			}

			if (Array.isArray(entry.columnWidths)) {
				const columnWidths = normalizeColumnWidths(
					entry.columnWidths as DataListColumnWidth[]
				);
				if (columnWidths.length > 0) {
					nextEntry.columnWidths = columnWidths;
				}
			}

			if (
				typeof entry.perPage === 'number' &&
				Number.isFinite(entry.perPage) &&
				entry.perPage > 0
			) {
				nextEntry.perPage = entry.perPage;
			}

			if (Object.keys(nextEntry).length > 0) {
				acc[key] = nextEntry;
			}

			return acc;
		},
		{}
	);
};

/**
 * Normalizes persisted files-table preferences payload.
 */
export const normalizeFilesTablePreferences = (
	value: unknown
): FilesTablePreferences => {
	if (!isRecord(value)) {
		return {};
	}

	return Object.entries(value).reduce<FilesTablePreferences>(
		(acc, [key, entry]) => {
			if (!isRecord(entry)) {
				return acc;
			}

			const nextEntry: FilesTablePreferences[string] = {};
			const rawSort = isRecord(entry.sort) ? entry.sort : null;
			if (
				rawSort &&
				isFilesTableSortPreference(rawSort.key) &&
				isFilesTableSortDirection(rawSort.direction)
			) {
				nextEntry.sort = {
					key: rawSort.key,
					direction: rawSort.direction,
				};
			} else if (isRecord(entry.groupSorts)) {
				const firstLegacySort = Object.values(entry.groupSorts).find(
					(sortValue) => isFilesTableSortPreference(sortValue)
				) as FilesTableSortPreference | undefined;
				if (firstLegacySort) {
					nextEntry.sort = {
						key: firstLegacySort,
						direction: getDefaultFilesTableSortDirection(firstLegacySort),
					};
				}
			}

			const metaVisibility = normalizeFilesTableMetaVisibility(
				entry.metaVisibility
			);
			if (metaVisibility.length > 0) {
				nextEntry.metaVisibility = metaVisibility;
			}

			if (Object.keys(nextEntry).length > 0) {
				acc[key] = nextEntry;
			}

			return acc;
		},
		{}
	);
};

/**
 * Normalizes split panel width map to positive finite numbers.
 */
export const normalizeSplitPanelWidths = (
	value: unknown
): SplitPanelWidthPreferences => {
	if (!isRecord(value)) {
		return {};
	}

	return Object.entries(value).reduce<SplitPanelWidthPreferences>(
		(acc, [key, width]) => {
			if (typeof width === 'number' && Number.isFinite(width) && width > 0) {
				acc[key] = width;
			}
			return acc;
		},
		{}
	);
};

/**
 * Normalizes advanced-filter layout map to supported layout modes.
 */
export const normalizeAdvancedFilterLayouts = (
	value: unknown
): AdvancedFilterLayoutPreferences => {
	if (!isRecord(value)) {
		return {};
	}

	return Object.entries(value).reduce<AdvancedFilterLayoutPreferences>(
		(acc, [key, layout]) => {
			if (layout === 'sidebar' || layout === 'collapse') {
				acc[key] = layout;
			}
			return acc;
		},
		{}
	);
};

/**
 * Normalizes advanced-filter collapse map to boolean values.
 */
export const normalizeAdvancedFilterCollapse = (
	value: unknown
): AdvancedFilterCollapsePreferences => {
	if (!isRecord(value)) {
		return {};
	}

	return Object.entries(value).reduce<AdvancedFilterCollapsePreferences>(
		(acc, [key, isOpen]) => {
			if (typeof isOpen === 'boolean') {
				acc[key] = isOpen;
			}
			return acc;
		},
		{}
	);
};

/**
 * Normalizes split-route memory map to valid route entries.
 */
export const normalizeSplitRouteMemory = (
	value: unknown
): SplitRouteMemoryPreferences => {
	if (!isRecord(value)) {
		return {};
	}

	return Object.entries(value).reduce<SplitRouteMemoryPreferences>(
		(acc, [key, entry]) => {
			if (
				!isRecord(entry) ||
				typeof entry.name !== 'string' ||
				!entry.name.trim()
			) {
				return acc;
			}

			acc[key] = {
				name: entry.name,
				params: isRecord(entry.params) ? entry.params : {},
				query: isRecord(entry.query) ? entry.query : {},
			};
			return acc;
		},
		{}
	);
};

/**
 * Reads initial color mode from localStorage with fallback to `light`.
 */
export const resolveInitialColorMode = (): ColorMode => {
	if (typeof window === 'undefined') {
		return 'light';
	}

	const saved = localStorage.getItem(COLOR_MODE_STORAGE_KEY);
	if (isColorMode(saved)) {
		return saved;
	}

	return 'light';
};

/**
 * Reads initial split-view flag from localStorage.
 */
export const resolveInitialSplitViewEnabled = (): boolean => {
	if (typeof window === 'undefined') {
		return false;
	}

	const saved = localStorage.getItem(SPLIT_VIEW_ENABLED_STORAGE_KEY);
	if (saved === 'false') {
		return false;
	}
	if (saved === 'true') {
		return true;
	}

	return false;
};

/**
 * Reads initial custom-fields-manager default-field visibility flag.
 */
export const resolveInitialCustomFieldsManagerShowDefaultFields =
	(): boolean => {
		if (typeof window === 'undefined') {
			return true;
		}

		const saved = localStorage.getItem(
			CUSTOM_FIELDS_MANAGER_DEFAULT_FIELDS_VISIBLE_STORAGE_KEY
		);
		if (saved === 'false') {
			return false;
		}
		if (saved === 'true') {
			return true;
		}

		return true;
	};

/**
 * Reads and normalizes persisted DataList preferences from localStorage.
 */
export const readDataListPreferences = (): DataListPreferences => {
	if (typeof localStorage === 'undefined') {
		return {};
	}

	try {
		const raw = localStorage.getItem(DATA_LIST_PREFERENCES_STORAGE_KEY);
		if (!raw) {
			return {};
		}
		const parsed = JSON.parse(raw) as DataListPreferences;
		return parsed && typeof parsed === 'object' ? parsed : {};
	} catch {
		return {};
	}
};

/**
 * Reads and normalizes persisted files-table preferences from localStorage.
 */
export const readFilesTablePreferences = (): FilesTablePreferences => {
	if (typeof localStorage === 'undefined') {
		return {};
	}

	try {
		const raw = localStorage.getItem(FILES_TABLE_PREFERENCES_STORAGE_KEY);
		if (!raw) {
			return {};
		}

		const parsed = JSON.parse(raw) as Record<string, unknown>;
		return normalizeFilesTablePreferences(parsed);
	} catch {
		return {};
	}
};

/**
 * Reads and normalizes persisted split panel widths from localStorage.
 */
export const readSplitPanelWidths = (): SplitPanelWidthPreferences => {
	if (typeof localStorage === 'undefined') {
		return {};
	}

	try {
		const raw = localStorage.getItem(SPLIT_PANEL_WIDTHS_STORAGE_KEY);
		if (!raw) {
			return {};
		}

		const parsed = JSON.parse(raw) as Record<string, unknown>;
		return normalizeSplitPanelWidths(parsed);
	} catch {
		return {};
	}
};

/**
 * Reads and normalizes persisted advanced-filter layout preferences.
 */
export const readAdvancedFilterLayouts =
	(): AdvancedFilterLayoutPreferences => {
		if (typeof localStorage === 'undefined') {
			return {};
		}

		try {
			const raw = localStorage.getItem(ADVANCED_FILTER_LAYOUTS_STORAGE_KEY);
			if (!raw) {
				return {};
			}

			const parsed = JSON.parse(raw) as Record<string, unknown>;
			return normalizeAdvancedFilterLayouts(parsed);
		} catch {
			return {};
		}
	};

/**
 * Reads and normalizes persisted advanced-filter collapse preferences.
 */
export const readAdvancedFilterCollapse =
	(): AdvancedFilterCollapsePreferences => {
		if (typeof localStorage === 'undefined') {
			return {};
		}

		try {
			const raw = localStorage.getItem(ADVANCED_FILTER_COLLAPSE_STORAGE_KEY);
			if (!raw) {
				return {};
			}

			const parsed = JSON.parse(raw) as Record<string, unknown>;
			return normalizeAdvancedFilterCollapse(parsed);
		} catch {
			return {};
		}
	};

/**
 * Reads and normalizes persisted split route memory preferences.
 */
export const readSplitRouteMemory = (): SplitRouteMemoryPreferences => {
	if (typeof localStorage === 'undefined') {
		return {};
	}

	try {
		const raw = localStorage.getItem(SPLIT_ROUTE_MEMORY_STORAGE_KEY);
		if (!raw) {
			return {};
		}

		const parsed = JSON.parse(raw) as Record<string, unknown>;
		return normalizeSplitRouteMemory(parsed);
	} catch {
		return {};
	}
};

/**
 * Applies color mode to the root document.
 */
export const applyColorMode = (mode: ColorMode): void => {
	if (typeof document === 'undefined') {
		return;
	}
	document.documentElement.setAttribute('data-color-mode', mode);
};

/**
 * Persists color mode to localStorage.
 */
export const persistColorMode = (mode: ColorMode): void => {
	if (typeof localStorage === 'undefined') {
		return;
	}
	localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
};

/**
 * Persists split-view enabled state to localStorage.
 */
export const persistSplitViewEnabled = (isEnabled: boolean): void => {
	if (typeof localStorage === 'undefined') {
		return;
	}

	localStorage.setItem(
		SPLIT_VIEW_ENABLED_STORAGE_KEY,
		String(Boolean(isEnabled))
	);
};

/**
 * Persists custom-fields-manager default-field visibility state to localStorage.
 */
export const persistCustomFieldsManagerShowDefaultFields = (
	isVisible: boolean
): void => {
	if (typeof localStorage === 'undefined') {
		return;
	}

	localStorage.setItem(
		CUSTOM_FIELDS_MANAGER_DEFAULT_FIELDS_VISIBLE_STORAGE_KEY,
		String(Boolean(isVisible))
	);
};

/**
 * Persists DataList preferences to localStorage.
 */
export const persistDataListPreferences = (
	preferences: DataListPreferences
): void => {
	if (typeof localStorage === 'undefined') {
		return;
	}

	localStorage.setItem(
		DATA_LIST_PREFERENCES_STORAGE_KEY,
		JSON.stringify(preferences)
	);
};

/**
 * Persists files-table preferences to localStorage.
 */
export const persistFilesTablePreferences = (
	preferences: FilesTablePreferences
): void => {
	if (typeof localStorage === 'undefined') {
		return;
	}

	localStorage.setItem(
		FILES_TABLE_PREFERENCES_STORAGE_KEY,
		JSON.stringify(preferences)
	);
};

/**
 * Persists split-panel width preferences to localStorage.
 */
export const persistSplitPanelWidths = (
	widths: SplitPanelWidthPreferences
): void => {
	if (typeof localStorage === 'undefined') {
		return;
	}

	localStorage.setItem(SPLIT_PANEL_WIDTHS_STORAGE_KEY, JSON.stringify(widths));
};

/**
 * Persists advanced-filter layout preferences to localStorage.
 */
export const persistAdvancedFilterLayouts = (
	preferences: AdvancedFilterLayoutPreferences
): void => {
	if (typeof localStorage === 'undefined') {
		return;
	}

	localStorage.setItem(
		ADVANCED_FILTER_LAYOUTS_STORAGE_KEY,
		JSON.stringify(preferences)
	);
};

/**
 * Persists advanced-filter collapse preferences to localStorage.
 */
export const persistAdvancedFilterCollapse = (
	preferences: AdvancedFilterCollapsePreferences
): void => {
	if (typeof localStorage === 'undefined') {
		return;
	}

	localStorage.setItem(
		ADVANCED_FILTER_COLLAPSE_STORAGE_KEY,
		JSON.stringify(preferences)
	);
};

/**
 * Persists split route memory preferences to localStorage.
 */
export const persistSplitRouteMemory = (
	preferences: SplitRouteMemoryPreferences
): void => {
	if (typeof localStorage === 'undefined') {
		return;
	}

	localStorage.setItem(
		SPLIT_ROUTE_MEMORY_STORAGE_KEY,
		JSON.stringify(preferences)
	);
};

const formatTimeZoneOffset = (offsetMinutes: number): string => {
	const sign = offsetMinutes >= 0 ? '+' : '-';
	const absoluteOffset = Math.abs(offsetMinutes);
	const hours = String(Math.floor(absoluteOffset / 60)).padStart(2, '0');
	const minutes = String(absoluteOffset % 60).padStart(2, '0');
	return `${sign}${hours}:${minutes}`;
};

/**
 * Resolves GMT offset label for an IANA timezone (example: `GMT+01:00`).
 */
export const resolveTimeZoneOffsetLabel = (
	timeZone: string,
	referenceDate: Date = new Date()
): string | null => {
	if (!timeZone || typeof Intl === 'undefined') {
		return null;
	}

	try {
		const utcDate = new Date(
			referenceDate.toLocaleString('en-US', {
				timeZone: 'UTC',
			})
		);
		const zonedDate = new Date(
			referenceDate.toLocaleString('en-US', {
				timeZone,
			})
		);
		const offsetMinutes = Math.round(
			(zonedDate.getTime() - utcDate.getTime()) / 60000
		);
		return `GMT${formatTimeZoneOffset(offsetMinutes)}`;
	} catch {
		return null;
	}
};

/**
 * Extracts the IANA timezone identifier from a display label.
 * Strips the `(GMT±HH:MM)` suffix if present.
 *
 * @example
 * extractIANATimeZone('Europe/Athens (GMT+03:00)') // 'Europe/Athens'
 * extractIANATimeZone('Europe/Athens') // 'Europe/Athens'
 */
export const extractIANATimeZone = (value: string): string => {
	const normalized = String(value || '').trim();
	if (!normalized) {
		return DEFAULT_TIME_ZONE;
	}

	// Strip the (GMT±HH:MM) suffix if present
	const match = normalized.match(/^(.+?)\s*\(GMT[+-]\d{2}:\d{2}\)$/);
	if (match) {
		return match[1].trim();
	}

	return normalized;
};

/**
 * Normalizes locally stored timezone values to API-accepted format:
 * `<IANA Timezone> (GMT±HH:MM)`.
 */
export const normalizeTimeZoneForUpsert = (value: string): string => {
	let normalized = String(value || '').trim();
	if (!normalized) {
		normalized = DEFAULT_TIME_ZONE;
	}

	if (/\(GMT[+-]\d{2}:\d{2}\)$/.test(normalized)) {
		return normalized;
	}

	if (
		(normalized === 'Browser Default' || normalized === 'UTC') &&
		typeof Intl !== 'undefined'
	) {
		normalized = Intl.DateTimeFormat().resolvedOptions().timeZone || normalized;
	}

	const gmtOffset = resolveTimeZoneOffsetLabel(normalized);
	if (gmtOffset) {
		return `${normalized} (${gmtOffset})`;
	}

	if (typeof Intl !== 'undefined') {
		const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		const browserOffset = resolveTimeZoneOffsetLabel(browserTimeZone);
		if (browserTimeZone && browserOffset) {
			return `${browserTimeZone} (${browserOffset})`;
		}
	}

	return normalized;
};

/**
 * Resolves the non-UI fields required to upsert user preferences.
 */
export const resolveUserPreferenceBasePayload =
	(): UserPreferenceBasePayload | null => {
		if (
			typeof sessionStorage === 'undefined' ||
			typeof localStorage === 'undefined'
		) {
			return null;
		}

		const userId = String(sessionStorage.getItem('user_id') || '').trim();
		if (!userId) {
			return null;
		}

		let timeZone =
			String(
				localStorage.getItem(PREFERRED_TIME_ZONE_STORAGE_KEY) || ''
			).trim() || DEFAULT_TIME_ZONE;
		if (timeZone === 'Browser Default' && typeof Intl !== 'undefined') {
			timeZone =
				Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIME_ZONE;
		}
		// Backend expects IANA timezone identifier only (e.g., "Europe/Athens"),
		// not the display label with GMT offset
		timeZone = extractIANATimeZone(timeZone);

		return {
			userId,
			dateFormat:
				String(
					localStorage.getItem(PREFERRED_DATE_FORMAT_STORAGE_KEY) || ''
				).trim() || DEFAULT_DATE_FORMAT,
			nameFormat:
				String(
					localStorage.getItem(PREFERRED_NAME_FORMAT_STORAGE_KEY) || ''
				).trim() || DEFAULT_NAME_FORMAT,
			timeZone,
			clockFormat:
				String(
					localStorage.getItem(PREFERRED_CLOCK_FORMAT_STORAGE_KEY) || ''
				).trim() || DEFAULT_CLOCK_FORMAT,
		};
	};
